/*
 * nc-fitting-room presets — the ONE place where each screen's fitting room is configured (table 4.3 of the "probador como
 * componente autónomo" proposal, approved by Javi on 2026-10-22 as PV-9). A screen picks a preset with
 * `<nc-fitting-room preset="brand-corner">`; any attribute written on the element (mode, model-source, tools, actions,
 * item-fields, views, chooser-position, head, add-position, look-limit) overrides the preset.
 *
 *   preset         mode  model-source  tools                        actions            item-fields        used by
 *   workspace      user  upload        clear,compare*               save,share*,add    price,link,total   SCR-010
 *   brand-hub      user  upload        clear,compare*               add,connect        price,link         SCR-012
 *   brand-corner   brand presets*      reset*,details (combine=off) connect            size,total         SCR-001
 *   entry          (inherits the current look)  none                (none)             (none)             "Probar" buttons (step 5, not built)
 *
 * Decisions of Javi (2026-10-22 21:28) encoded here:
 *   (a) mode "brand" uses the brand's OWN model photos and generates NOTHING with AI: usesRender() is false, so the component
 *       makes no render, quota or consent call and requires no session. Mode "user" follows assets/nc-tryon-config.js through
 *       gateway.getTryOnConfig() (session, quota, max garments are never hardcoded here).
 *   (d) CONFIRMED by Javi (22 Oct 21:39): Limpiar works (Deshacer / Rehacer BUTTONS were removed on 24 Oct, see below; the history stays
 *       behind the toasts); Detalles and Comparar only fire `nc-fitting:tool` and say "not available yet" (no new behaviour). Aleatorio and Ajustar are REMOVED everywhere.
 *       Comparar is user mode only: resolve() drops it in mode "brand".
 *   (f) Brand mode shows the brand model's own photo with the look's garments laid out one by one over it; still no render, quota or
 *       consent. The add / remove / undo / redo core stays available behind `combine`: "off" in A1 (brand-corner keeps its tools
 *       reset,details and action connect); `combine="on"` adds clear and the "add" action in brand mode without touching
 *       any other code, for the day the user may combine garments there.
 *   (e) item fields are configuration: price + link (user) or size + total (brand). The gateway look item carries no chosen
 *       size yet (variantId is not resolved): the row falls back to the price when no size is known.
 *
 * UX pruning of the fitting room (audit docs/normcore-probador-friccion-ux.md, approved by Javi on 2026-10-24):
 *   QUITAR   the canvas view rail (Ver avatar / prendas / guardados / calzado / bolsos): `views: []` in every preset; the "Ver avatar"
 *            zoom reset is gone.
 *   ESCONDER HASTA INTEGRAR  (* in the table) Comparar, Compartir look, the preset models (Nora / Lucía / Amina chips and the
 *            "Cambiar modelo" model panel / sheet that only listed them) in EVERY mode, and Reiniciar. ONE switch re-enables them:
 *            PENDING.on below. While it is true, resolve() drops PENDING.tools and PENDING.actions in mode "user", drops PENDING.anyTools
 *            (Reiniciar) in every mode and sets cfg.modelPresets = false in every mode. In user mode the person icon and the desktop button
 *            open the ONE photo dialog ("Tu foto" / "Cambiar foto") on every width; in brand mode (Branding Corner) the canvas shows NO model
 *            selector at all (modelSource is effectively 'none' until the brand models have photos; the first model is still loaded
 *            silently so the look and the garments over her photo keep working). The code paths (compare event, onShare, the models sheet,
 *            the preset chips) stay intact behind it. "Detalles" stays in brand mode: it never depended on the presets.
 *   B3 (2026-10-27): in user mode the preset chips are off for good (modelPresets is false in user mode even with PENDING.on false); the
 *            four synthetic models of DP-1 b are offered inside the photo dialog instead ("Elige un modelo", nc-fitting-room.js).
 *
 * Zoom, Deshacer and Rehacer (Javi, 2026-10-24): the canvas has NO zoom (slider, - / +, pinch) in any mode and "Mi look actual" has NO
 *   standalone Deshacer / Rehacer buttons; only Limpiar (44 x 44) stays. "Reiniciar" only reset the zoom, so it is behind PENDING.anyTools.
 *   The undo / redo history stays in nc-fitting-room.js: the "Look vaciado" and slot-rule replacement toasts use it for their Deshacer.
 */
(function (root) {
  // tool catalogue: group = which column it belongs to; behaviour = what activating it does today
  var TOOLS = {
    clear: { group: 'history', behaviour: 'history' },
    compare: { group: 'history', behaviour: 'unavailable', userOnly: true },
    reset: { group: 'view', behaviour: 'unavailable' },
    details: { group: 'view', behaviour: 'unavailable' }
  };
  // order used to draw the tools of each group, whatever the order written in `tools`
  var TOOL_ORDER = ['clear', 'compare', 'reset', 'details'];

  // ESCONDER HASTA INTEGRAR (see the header): set `on` to false to bring Comparar, Compartir look and the user-mode preset models back
  var PENDING = { on: true, tools: ['compare'], actions: ['share'], modelPresets: true, anyTools: ['reset'] };

  var PRESETS = {
    workspace: {
      ui: true, mode: 'user', combine: 'on', modelSource: 'upload', tools: ['clear', 'compare'], views: [],
      actions: ['save', 'share', 'add'], itemFields: ['price', 'link', 'total'], chooserPosition: 'between', head: 'hidden', addPosition: 'after-primary', lookLimit: 4
    },
    'brand-hub': {
      ui: true, mode: 'user', combine: 'on', modelSource: 'upload', tools: ['clear', 'compare'], views: [],
      actions: ['add', 'connect'], itemFields: ['price', 'link'], chooserPosition: 'before', head: 'title', addPosition: 'before-primary', lookLimit: 4
    },
    'brand-corner': {
      ui: true, mode: 'brand', modelSource: 'presets', tools: ['reset', 'details'], combine: 'off', views: [],
      actions: ['connect'], itemFields: ['size', 'total'], chooserPosition: 'before', head: 'title-count', addPosition: 'after-primary', lookLimit: 4
    },
    // the entry points ("Probar en mi look" in SCR-003, community, history) have no canvas: they only add to the current look (step 5)
    entry: {
      ui: false, mode: 'inherit', combine: 'on', modelSource: 'none', tools: [], views: [], actions: [], itemFields: [], chooserPosition: 'after', head: 'hidden', addPosition: 'after-primary', lookLimit: 0
    }
  };

  var MODES = ['user', 'brand', 'inherit'];
  var SOURCES = ['upload', 'presets', 'none'];

  function list(value) {
    if (Array.isArray(value)) return value.map(function (x) { return String(x).trim(); }).filter(Boolean);
    return String(value == null ? '' : value).split(',').map(function (x) { return x.trim(); }).filter(Boolean);
  }

  // preset + explicit overrides -> normalised configuration. Unknown preset names fall back to "workspace"; unknown ids are dropped.
  function resolve(name, overrides) {
    var base = PRESETS[name] || PRESETS.workspace;
    var o = overrides || {};
    var has = function (k) { return o[k] !== undefined && o[k] !== null && o[k] !== ''; };
    var cfg = {
      preset: PRESETS[name] ? name : 'workspace',
      ui: base.ui,
      mode: has('mode') && MODES.indexOf(o.mode) >= 0 ? o.mode : base.mode,
      modelSource: has('modelSource') && SOURCES.indexOf(o.modelSource) >= 0 ? o.modelSource : base.modelSource,
      tools: (has('tools') ? list(o.tools) : base.tools.slice()).filter(function (t) { return TOOLS[t]; }),
      views: has('views') ? list(o.views) : base.views.slice(),
      actions: (has('actions') ? list(o.actions) : base.actions.slice()).filter(function (a) { return ['save', 'share', 'add', 'connect'].indexOf(a) >= 0; }),
      itemFields: (has('itemFields') ? list(o.itemFields) : base.itemFields.slice()).filter(function (f) { return ['price', 'link', 'size', 'total'].indexOf(f) >= 0; }),
      chooserPosition: has('chooserPosition') && ['before', 'between', 'after'].indexOf(o.chooserPosition) >= 0 ? o.chooserPosition : base.chooserPosition,
      head: has('head') && ['hidden', 'title', 'title-count'].indexOf(o.head) >= 0 ? o.head : base.head,
      addPosition: has('addPosition') && ['before-primary', 'after-primary'].indexOf(o.addPosition) >= 0 ? o.addPosition : base.addPosition,
      combine: has('combine') && ['on', 'off'].indexOf(o.combine) >= 0 ? o.combine : base.combine,
      lookLimit: has('lookLimit') && isFinite(parseInt(o.lookLimit, 10)) ? Math.max(0, parseInt(o.lookLimit, 10)) : base.lookLimit,
      modelPresets: true
    };
    // ESCONDER HASTA INTEGRAR: one switch (PENDING.on) for Comparar and Compartir look (user mode), Reiniciar and the preset models (every mode)
    if (PENDING.on) {
      if (cfg.mode === 'user') {
        cfg.tools = cfg.tools.filter(function (t) { return PENDING.tools.indexOf(t) < 0; });
        cfg.actions = cfg.actions.filter(function (a) { return PENDING.actions.indexOf(a) < 0; });
      }
      cfg.tools = cfg.tools.filter(function (t) { return PENDING.anyTools.indexOf(t) < 0; });
      if (PENDING.modelPresets) {
        cfg.modelPresets = false;
        if (cfg.modelSource === 'presets') cfg.modelSource = 'none'; // no photos yet: no model selector at all
      }
    }
    // B3 (DP-1 b, Javi 2026-10-27): in user mode the preset models live ONLY in the photo step ("Elige un modelo" of the "Tu foto" dialog,
    // four synthetic people uploaded as the user's photo). The old Nora / Lucía / Amina chips and models sheet never come back in user
    // mode, whatever PENDING says; PENDING.modelPresets keeps governing brand mode only.
    if (cfg.mode === 'user') cfg.modelPresets = false;
    if (cfg.mode === 'brand') {
      cfg.tools = cfg.tools.filter(function (t) { return !TOOLS[t].userOnly; });
      if (cfg.combine === 'on') {
        if (cfg.tools.indexOf('clear') < 0) cfg.tools.push('clear');
        if (cfg.actions.indexOf('add') < 0) cfg.actions.push('add');
      } else {
        // combine off: no Limpiar and no "add" in brand mode (the removal x of a row stays, as in SCR-001 today)
        cfg.tools = cfg.tools.filter(function (t) { return t !== 'clear'; });
        cfg.actions = cfg.actions.filter(function (a) { return a !== 'add'; });
      }
    }
    cfg.tools.sort(function (a, b) { return TOOL_ORDER.indexOf(a) - TOOL_ORDER.indexOf(b); });
    return cfg;
  }

  // Decision (a): only "user" mode talks to the render pipeline (session, consent, quota, garment limit from getTryOnConfig).
  // "brand" shows the brand's own model photos and never calls the gateway's render, quota or consent methods.
  function usesRender(cfg) { return cfg.mode === 'user'; }

  function toolsOf(cfg, group) { return cfg.tools.filter(function (t) { return TOOLS[t].group === group; }); }

  var api = { PRESETS: PRESETS, PENDING: PENDING, TOOLS: TOOLS, TOOL_ORDER: TOOL_ORDER, resolve: resolve, usesRender: usesRender, toolsOf: toolsOf, list: list };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.NcFittingRoomPresets = api;
})(typeof window !== 'undefined' ? window : this);
